import React, { useEffect, useState, useContext } from "react";
import {
  Card,
  Row,
  Col,
  Statistic,
  Tag,
  Table,
  message,
  Typography,
  Spin,
  Button,
  Modal,
  Space,
  Popconfirm,
} from "antd";
import { AppContext } from "../context/AppContext";
import {
  SafetyCertificateOutlined,
  CheckCircleOutlined,
  DollarCircleOutlined,
  CloseCircleOutlined,
  BarcodeOutlined,
  StopOutlined,
} from "@ant-design/icons";
import axios from "axios";

const { Title, Text } = Typography;

const Giftcards = () => {
  const { company } = useContext(AppContext);
  const [giftcards, setGiftcards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deactivatingCard, setDeactivatingCard] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    showSizeChanger: true,
    showQuickJumper: true,
  });
  const token = localStorage.getItem("token");

  const fetchGiftcards = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        "https://zedlenders.pickmesms.com/items/vouchers?fields=*,owner.id,owner.first_name,owner.last_name",
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      setGiftcards(response.data.data);
    } catch (err) {
      console.error("Error fetching giftcards:", err);
      setError("Failed to load gift cards");
      message.error("Error loading gift card data");
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivateCard = async (card) => {
    try {
      setDeactivatingCard(card.id);
      console.log("Deactivating card:", card);

      // First, check if the owner exists and is properly structured
      if (card.owner && card.owner.id) {
        console.log("Updating owner with ID:", card.owner.id);

        try {
          // Get current user data to see if voucher field is an array or single value
          const userResponse = await axios.get(
            `https://zedlenders.pickmesms.com/users/${card.owner.id}?fields=voucher`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );

          //console.log("Current user data:", userResponse.data.data);

          // Update the user to remove the voucher reference - handle both array and single value cases
          await axios.patch(
            `https://zedlenders.pickmesms.com/users/${card.owner.id}`,
            {
              voucher: null, // Remove the voucher reference
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );

          console.log("User updated successfully");
        } catch (userError) {
          console.error("Error updating user:", userError);
          // Continue with voucher update even if user update fails
        }
      } else {
        console.log("No owner or owner.id found for this card");
      }

      // Then update the voucher status to not_assigned and clear owner
      console.log("Updating voucher with ID:", card.id);
      const voucherResponse = await axios.patch(
        `https://zedlenders.pickmesms.com/items/vouchers/${card.id}`,
        {
          status: "not_assigned",
          owner: null,
          user_id: null,
          date_issued: null,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      console.log("Voucher update response:", voucherResponse.data);

      message.success("Gift card deactivated successfully");
      // Refresh gift cards list
      fetchGiftcards();
    } catch (err) {
      console.error("Error deactivating gift card:", err);
      message.error(
        "Failed to deactivate gift card: " +
          (err.response?.data?.errors?.[0]?.message || err.message)
      );
    } finally {
      setDeactivatingCard(null);
    }
  };

  useEffect(() => {
    fetchGiftcards();
  }, [token]);

  // Calculate summary statistics
  const stats = {
    total: giftcards.length,
    active: giftcards.filter((card) => card.status === "active").length,
    used: giftcards.filter((card) => card.status === "used").length,
    inactive: giftcards.filter((card) => card.status === "not_assigned").length,

    totalValue: giftcards.reduce(
      (sum, card) => sum + (parseFloat(card.amount) || 0),
      0
    ),

    redeemedValue: giftcards
      .filter((card) => card.status === "used")
      .reduce((sum, card) => sum + (card.amount || 0), 0),
  };

  const columns = [
    {
      title: "Card Number",
      dataIndex: "card_number",
      key: "card_number",
      render: (text) => (
        <span>
          <BarcodeOutlined /> **** **** **** {text?.slice(-4)}
        </span>
      ),
      sorter: (a, b) => a.card_number.localeCompare(b.card_number),
    },
    {
      title: "Store",
      dataIndex: "store",
      key: "store",
      render: (text) => text || "N/A",
      sorter: (a, b) => a.store.name?.localeCompare(b.store.name),
    },
    {
      title: "Issued Date",
      dataIndex: "date_issued",
      key: "date_issued",
      render: (date) => (date ? new Date(date).toLocaleDateString() : "N/A"),
      sorter: (a, b) => new Date(a.date_issued) - new Date(b.date_issued),
    },
    {
      title: "Amount (ZMW)",
      dataIndex: "amount",
      key: "amount",
      render: (amount) => (
        <strong>
          {amount?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </strong>
      ),
      sorter: (a, b) => a.amount - b.amount,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const statusConfig = {
          active: { color: "green", label: "Active" },
          used: { color: "red", label: "Redeemed" },
          not_assigned: { color: "gray", label: "Inactive" },
        };
        const config = statusConfig[status] || {
          color: "gray",
          label: "Unknown",
        };
        return <Tag color={config.color}>{config.label}</Tag>;
      },
      filters: [
        { text: "Active", value: "active" },
        { text: "Redeemed", value: "used" },
        { text: "Inactive", value: "not_assigned" },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: "Owner",
      dataIndex: "owner",
      key: "owner",
      render: (owner, record) =>
        owner ? (
          <Tag color="blue">
            {record.owner.first_name + " " + record.owner.last_name}
          </Tag>
        ) : (
          <Text type="secondary">Not assigned</Text>
        ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          {record.status === "active" && (
            <Popconfirm
              title="Deactivate this gift card?"
              description="This will remove the card from the borrower's account. Are you sure?"
              onConfirm={() => handleDeactivateCard(record)}
              okText="Yes"
              cancelText="No"
            >
              <Button
                danger
                type="primary"
                size="small"
                icon={<StopOutlined />}
                loading={deactivatingCard === record.id}
              >
                Deactivate
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  if (error)
    return (
      <Card>
        <Text type="danger">{error}</Text>
      </Card>
    );

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 24 }}>
        Gift Card Management
      </Title>

      <Spin spinning={loading}>
        {/* Summary Statistics Row */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card bordered={false}>
              <Statistic
                title="Total Cards"
                value={stats.total}
                prefix={<SafetyCertificateOutlined />}
                valueStyle={{ color: "#1890ff" }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card bordered={false}>
              <Statistic
                title="Active Cards"
                value={stats.active}
                prefix={<CheckCircleOutlined />}
                valueStyle={{ color: "#52c41a" }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card bordered={false}>
              <Statistic
                title="Total Value Issued"
                value={stats.totalValue}
                prefix={<DollarCircleOutlined />}
                valueStyle={{ color: "#9254de" }}
                formatter={(value) => value.toLocaleString()}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card bordered={false}>
              <Statistic
                title="Redeemed Value"
                value={stats.redeemedValue}
                prefix={<CloseCircleOutlined />}
                valueStyle={{ color: "#cf1322" }}
                formatter={(value) => value.toLocaleString()}
              />
            </Card>
          </Col>
        </Row>

        {/* Main Data Table */}
        <Card
          title="Gift Card Details"
          bordered={false}
          headStyle={{ borderBottom: "1px solid #f0f0f0" }}
        >
          <Table
            columns={columns}
            dataSource={giftcards}
            rowKey="id"
            pagination={{
              ...pagination,
              total: giftcards.length,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} items`,
              onChange: (page, pageSize) => {
                setPagination(prev => ({
                  ...prev,
                  current: page,
                  pageSize: pageSize,
                }));
              },
            }}
            scroll={{ x: true }}
            bordered
            size="middle"
          />
        </Card>
      </Spin>
    </div>
  );
};

export default Giftcards;
